/**
 * useComments Hook
 *
 * React Query hooks for investment comments (communication log) operations
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';

import {
  commentsService,
  CreateCommentDto,
  UpdateCommentDto,
  DeleteCommentResponse,
} from '../services/comments.service';
import { unwrapResponse } from '../types';

import type { InvestmentComment, InvestmentCommentWithReplies } from '@medsuite/models';

export const commentKeys = {
  all: ['comments'] as const,
  lists: () => [...commentKeys.all, 'list'] as const,
  list: (investmentId: number) => [...commentKeys.lists(), investmentId] as const,
  threaded: (investmentId: number) => [...commentKeys.list(investmentId), 'threaded'] as const,
  details: () => [...commentKeys.all, 'detail'] as const,
  detail: (investmentId: number, commentId: number) =>
    [...commentKeys.details(), investmentId, commentId] as const,
  replies: (investmentId: number, commentId: number) =>
    [...commentKeys.detail(investmentId, commentId), 'replies'] as const,
};

/**
 * Fetch all comments for an investment (flat list, sorted by date desc)
 */
export function useComments(
  investmentId: number,
  options?: Omit<UseQueryOptions<InvestmentComment[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<InvestmentComment[], Error>({
    queryKey: commentKeys.list(investmentId),
    queryFn: async () => {
      const response = await commentsService.getComments(investmentId);
      // Extract data from success wrapper
      const responseData = unwrapResponse<{ comments?: InvestmentComment[] }>(response.data);
      return responseData.comments || [];
    },
    ...options,
  });
}

/**
 * Fetch threaded comments for an investment (nested structure)
 */
export function useThreadedComments(
  investmentId: number,
  options?: Omit<UseQueryOptions<InvestmentCommentWithReplies[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<InvestmentCommentWithReplies[], Error>({
    queryKey: commentKeys.threaded(investmentId),
    queryFn: async () => {
      const response = await commentsService.getThreadedComments(investmentId);
      // Extract data from success wrapper
      const responseData = unwrapResponse<{ comments?: InvestmentCommentWithReplies[] }>(response.data);
      return responseData.comments || [];
    },
    ...options,
  });
}

/**
 * Add a new comment
 */
export function useAddComment(
  investmentId: number,
  options?: UseMutationOptions<InvestmentComment, Error, CreateCommentDto>
) {
  const queryClient = useQueryClient();

  return useMutation<InvestmentComment, Error, CreateCommentDto>({
    mutationFn: async (data) => {
      const response = await commentsService.addComment(investmentId, data);
      return unwrapResponse<InvestmentComment>(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commentKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: commentKeys.threaded(investmentId) });
    },
    ...options,
  });
}

/**
 * Add a reply to an existing comment
 */
export function useAddReply(
  investmentId: number,
  options?: UseMutationOptions<
    InvestmentComment,
    Error,
    { parentCommentId: number; data: Omit<CreateCommentDto, 'parent_comment_id'> }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<
    InvestmentComment,
    Error,
    { parentCommentId: number; data: Omit<CreateCommentDto, 'parent_comment_id'> }
  >({
    mutationFn: async ({ parentCommentId, data }) => {
      const response = await commentsService.addReply(investmentId, parentCommentId, data);
      return unwrapResponse<InvestmentComment>(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commentKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: commentKeys.threaded(investmentId) });
    },
    ...options,
  });
}

/**
 * Update a comment's text
 */
export function useUpdateComment(
  investmentId: number,
  options?: UseMutationOptions<
    InvestmentComment,
    Error,
    { commentId: number; data: UpdateCommentDto }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<InvestmentComment, Error, { commentId: number; data: UpdateCommentDto }>({
    mutationFn: async ({ commentId, data }) => {
      const response = await commentsService.updateComment(investmentId, commentId, data);
      return unwrapResponse<InvestmentComment>(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commentKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: commentKeys.threaded(investmentId) });
    },
    ...options,
  });
}

/**
 * Delete a comment
 */
export function useDeleteComment(
  investmentId: number,
  options?: UseMutationOptions<DeleteCommentResponse, Error, number>
) {
  const queryClient = useQueryClient();

  return useMutation<DeleteCommentResponse, Error, number>({
    mutationFn: async (commentId) => {
      const response = await commentsService.deleteComment(investmentId, commentId);
      return unwrapResponse<DeleteCommentResponse>(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commentKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: commentKeys.threaded(investmentId) });
    },
    ...options,
  });
}

/**
 * Get replies to a specific comment
 */
export function useCommentReplies(
  investmentId: number,
  commentId: number,
  options?: Omit<UseQueryOptions<InvestmentComment[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<InvestmentComment[], Error>({
    queryKey: commentKeys.replies(investmentId, commentId),
    queryFn: async () => {
      const response = await commentsService.getReplies(investmentId, commentId);
      const responseData = unwrapResponse<{ replies?: InvestmentComment[] }>(response.data);
      return responseData.replies || [];
    },
    ...options,
  });
}
